import React, { useMemo, useState } from 'react';
import {
  Book,
  BookOpen,
  Clock,
  GraduationCap,
  Home,
  Layers,
  Users,
} from 'lucide-react';
import type { FacultyMember } from '../../types/faculty';

interface FacultyTeachingTabProps {
  faculty: FacultyMember;
}

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const;
const periods = [1, 2, 3, 4, 5, 6, 7, 8];

export function FacultyTeachingTab({ faculty }: FacultyTeachingTabProps) {
  const [scheduleWeek, setScheduleWeek] = useState<'this' | 'next'>('this');
  const assignment = faculty.teachingAssignment;

  if (!assignment) {
    return (
      <div className="rounded-xl border border-border bg-white p-12 text-center shadow-sm">
        <BookOpen className="mx-auto mb-4 h-12 w-12 text-text-tertiary" />
        <h3 className="mb-2 font-heading text-lg font-semibold text-iron-grey">No teaching assignment</h3>
        <p className="text-text-secondary">
          This faculty member does not have an active teaching assignment.
        </p>
      </div>
    );
  }

  const activeSchedule = scheduleWeek === 'next' ? assignment.nextWeekSchedule ?? assignment.schedule : assignment.schedule;

  const summary = useMemo(() => {
    let teachingCount = 0;
    let prepCount = 0;
    let dutyCount = 0;

    Object.values(activeSchedule).forEach((daySchedule) => {
      daySchedule.forEach((entry) => {
        if (entry.type === 'teaching') teachingCount += 1;
        if (entry.type === 'prep') prepCount += 1;
        if (entry.type === 'duty') dutyCount += 1;
      });
    });

    return { teachingCount, prepCount, dutyCount };
  }, [activeSchedule]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Current assignment</h3>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-muted p-2 text-jungle">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Division(s)</p>
              <p className="text-sm font-medium text-iron-grey">{assignment.divisions.join(', ') || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-success-background p-2 text-jungle">
              <GraduationCap className="h-5 w-5" />
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Grades</p>
              <p className="text-sm font-medium text-iron-grey">{assignment.grades.join(', ') || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-muted p-2 text-jungle">
              <Book className="h-5 w-5" />
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Subjects</p>
              <p className="text-sm font-medium text-iron-grey">{assignment.subjects.join(', ') || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-success-background p-2 text-aquamarine">
              <Home className="h-5 w-5" />
            </div>
            <div>
              <p className="mb-1 font-title text-xs uppercase tracking-wider text-text-secondary">Homeroom</p>
              <p className="text-sm font-medium text-iron-grey">{assignment.homeroom || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-iron-grey">Schedule</h3>
            <div className="flex rounded-lg bg-muted p-1">
              <button
                type="button"
                className={`rounded-md px-3 py-1 text-sm font-heading font-medium transition-colors ${
                  scheduleWeek === 'this' ? 'bg-white text-primary shadow-sm' : 'text-text-secondary hover:text-iron-grey'
                }`}
                onClick={() => setScheduleWeek('this')}
              >
                This week
              </button>
              <button
                type="button"
                className={`rounded-md px-3 py-1 text-sm font-heading font-medium transition-colors ${
                  scheduleWeek === 'next' ? 'bg-white text-primary shadow-sm' : 'text-text-secondary hover:text-iron-grey'
                }`}
                onClick={() => setScheduleWeek('next')}
              >
                Next week
              </button>
            </div>
          </div>

          <div className="mb-4 flex-1 overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th className="w-16 border-b border-r border-border bg-muted p-2 text-center font-title text-xs font-normal uppercase text-text-secondary">
                    Period
                  </th>
                  {days.map((day) => (
                    <th
                      key={day}
                      className="min-w-[120px] border-b border-border bg-muted p-2 text-center font-body text-xs font-normal uppercase text-text-secondary"
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map((period) => (
                  <tr key={period}>
                    <td className="border-b border-r border-border bg-background-light p-2 text-center text-xs font-medium text-text-secondary">
                      {period}
                    </td>
                    {days.map((day) => {
                      const entry = activeSchedule[day]?.find((candidate) => candidate.period === period);
                      let cellClass = 'h-16 border-b border-border p-2 text-center ';
                      let textClass = 'text-sm font-medium ';

                      if (!entry) {
                        cellClass += 'bg-white';
                      } else if (entry.type === 'prep') {
                        cellClass += 'bg-muted';
                        textClass += 'italic text-text-secondary';
                      } else if (entry.type === 'duty') {
                        cellClass += 'bg-warning-background';
                        textClass += 'text-coral';
                      } else {
                        cellClass += 'bg-muted';
                        textClass += 'text-iron-grey';
                      }

                      return (
                        <td key={`${day}-${period}`} className={cellClass}>
                          {entry ? (
                            <div className="flex h-full flex-col items-center justify-center">
                              <span className={textClass}>{entry.subject}</span>
                              {entry.grade ? (
                                <span className={`text-xs ${entry.type === 'teaching' ? 'text-iron-grey' : 'text-text-tertiary'}`}>
                                  {entry.grade}
                                </span>
                              ) : null}
                              {entry.room ? (
                                <span className={`text-xs ${entry.type === 'teaching' ? 'text-text-secondary' : 'text-text-tertiary'}`}>
                                  {entry.room}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-4 border-t border-border pt-2 font-title text-xs uppercase tracking-wider text-text-secondary">
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded bg-muted" />
              <span>Teaching</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded border border-border bg-muted" />
              <span>Prep</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded bg-warning-background" />
              <span>Duty</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-muted p-3">
                <div className="flex items-center text-text-secondary">
                  <Clock className="mr-3 h-5 w-5" />
                  <span className="text-sm font-medium">Teaching periods</span>
                </div>
                <span className="font-heading text-xl font-bold text-text-tertiary">{summary.teachingCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-success-background p-3">
                <div className="flex items-center text-jungle">
                  <BookOpen className="mr-3 h-5 w-5" />
                  <span className="text-sm font-medium">Prep periods</span>
                </div>
                <span className="font-heading text-xl font-bold text-jungle">{summary.prepCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-warning-background p-3">
                <div className="flex items-center text-coral">
                  <Users className="mr-3 h-5 w-5" />
                  <span className="text-sm font-medium">Duty assignments</span>
                </div>
                <span className="font-heading text-xl font-bold text-coral">{summary.dutyCount}</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">Class rosters</h3>
            <div className="space-y-3">
              {assignment.classes.map((classroom, index) => (
                <button
                  key={`${classroom.subject}-${classroom.grade}-${index}`}
                  type="button"
                  className="group flex w-full items-center justify-between rounded-lg border border-border p-3 text-left shadow-sm transition-all duration-200 hover:border-primary hover:bg-muted/30 hover:shadow"
                >
                  <div>
                    <p className="text-sm font-medium text-iron-grey transition-colors group-hover:text-text-secondary">
                      {classroom.subject || 'Unassigned'}
                    </p>
                    <p className="mt-0.5 text-xs text-text-secondary">{classroom.grade || '—'}</p>
                  </div>
                  <div className="flex items-center rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-white shadow-sm">
                    <Users className="mr-1.5 h-3.5 w-3.5" />
                    {classroom.studentCount}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
